import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { useLocation } from "react-router-dom";

// Rutas principales
const MAIN_MODULES = [
  "/home",
  "/admin",
  "/concursos",
  "/nomina",
  "/nominaPDF",
  "/irpfPDF",
  "/detallePersonal",
  "/misActosAdmin",
  "/irpfHabilitado",
  "/listadoactos"
];

const FOOTER_ROUTES = [
  "/accesibilidad",
  "/avisoLegal",
  "/mapaWeb"
];

const HistoryContext = createContext();

// 🧱 Prefijo según entorno (dev, pre, prod)
const ENV = process.env.REACT_APP_ENV || "dev";
const STORAGE_KEY = `breadcrumbsHistory_${ENV}`;

export const HistoryProvider = ({ children }) => {
  const location = useLocation();

  // 🔒 Fallback si localStorage no está disponible
  const safeStorage = (() => {
    try {
      localStorage.setItem("test", "1");
      localStorage.removeItem("test");
      return localStorage;
    } catch (err) {
      
      return sessionStorage;
    }
  })();

  // 📦 Cargar historial desde almacenamiento
  const [history, setHistory] = useState(() => {
    try {
      const saved = safeStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      
      safeStorage.removeItem(STORAGE_KEY);
      return [];
    }
  });

  // 💾 Guardar historial cuando cambie
  useEffect(() => {
    try {
      safeStorage.setItem(STORAGE_KEY, JSON.stringify(history));
    } catch (err) {
      // console.error("Error guardando breadcrumbs en almacenamiento:", err);
    }
  }, [history, safeStorage]);

  // 📌 Actualizar historial
  const updateHistory = useCallback((newPath) => {
    setHistory((prevHistory) => {
      const existingIndex = prevHistory.indexOf(newPath);
      if (existingIndex !== -1) {
        // Ya existe → recorta hasta ese punto
        return prevHistory.slice(0, existingIndex + 1);
      } else {
        // Nuevo → lo agrega
        return [...prevHistory, newPath];
      }
    });
  }, []);

  // 🧹 Limpiar historial completamente
  const clearHistory = useCallback(() => {
    setHistory([]);
    safeStorage.removeItem(STORAGE_KEY);
  }, [safeStorage]);

  // 🔄 Reiniciar historial a /home si navegas a /home
  const resetHistory = useCallback(() => {
    const initial = ["/home"];
    setHistory(initial);
    safeStorage.setItem(STORAGE_KEY, JSON.stringify(initial));
  }, [safeStorage]);

  // 🔄 Monitorear cambios de ruta
  useEffect(() => {
    const current = location.pathname;

    // ⚙️ Si entramos a una ruta del footer
    if (FOOTER_ROUTES.includes(current)) {
      setHistory([current]);
      safeStorage.setItem(STORAGE_KEY, JSON.stringify([current]));
      return;
    }

    // 🌐 Comprobar si es una ruta principal
    const cleanCurrent = current.replace(/\/$/, "").split("?")[0];

    const rootModule = MAIN_MODULES.find(
      (mod) =>
        cleanCurrent === mod ||
        cleanCurrent.startsWith(mod + "/")
    );

    // 🟦 Si entro DIRECTAMENTE a un módulo principal
    // Ej: /misActosAdmin → historial correcto: ["/misActosAdmin"]
    if (rootModule && current === rootModule) {
      const initial = ["/home", current];   // ⬅⬅ AÑADIMOS HOME COMO RAÍZ SIEMPRE
      setHistory(initial);
      safeStorage.setItem(STORAGE_KEY, JSON.stringify(initial));
      return;
    }

    // 🟩 Si navego dentro de un módulo → agrego ruta
    updateHistory(current);

  }, [location.pathname, resetHistory, updateHistory, safeStorage]);

  return (
    <HistoryContext.Provider
      value={{ history, updateHistory, clearHistory, resetHistory }}
    >
      {children}
    </HistoryContext.Provider>
  );
};

export const useHistoryContext = () => useContext(HistoryContext);
